import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	Button, Card, Empty, Input, Modal, Select, Switch, Table
} from 'antd'
import i18next from 'i18next'
import { debounce, get } from 'lodash'
import { useTranslation } from 'react-i18next'
import { PlusCircleOutlined, PrinterOutlined } from '@ant-design/icons'
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table'
import { FilterValue, SorterResult } from 'antd/lib/table/interface'
import {
	BooleanParam, NumberParam, NumericArrayParam, StringParam, useQueryParams, withDefault
} from 'use-query-params'
import { getPriceOfferHeaderOptions, IFirmHeader } from '../../reducers/priceOffers/actions'
import { IJobFirmHeader } from '../../reducers/jobs/actions'
import { switchProfile } from '../../reducers/user/userActions'
import { history } from '../../utils/history'
import { RootState } from '../../reducers'
import { PAGINATION, USER_ROLE } from '../../utils/enums'
import SelectUsersField from '../../components/SelectUsersField'
import { useSuperOrganizationMode } from '../../utils/superOrganizationMode'
import {
	renderOrganizationTag, renderQrScanDuration, renderQrScanLastService, renderTrustees
} from '../jobs/components/jobColumnRenderers'
import { SuperOrganizationJobTableItem, useSuperOrganizationJobs } from '../../queries/superOrganization/useSuperOrganizationJobs'
import PrintColumnsModal from './components/PrintColumnsModal'

// specialna volba v selecte "Filter podľa s.r.o." - zakazky bez priradenej s.r.o.
const WITHOUT_FIRM_HEADER = -1

// antd sorter.field (= dataIndex) -> hodnota orderBy na API
const SORTER_FIELD_TO_ORDER_BY: Record<string, string> = {
	address: 'address',
	organization: 'organizationName',
	firmHeader: 'firmName'
}

type Columns = ColumnsType<SuperOrganizationJobTableItem>

const formatEur = (value: number) => `${(Math.round(value * 100) / 100).toLocaleString('sk-SK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`

const SuperOrganizationJobs = () => {
	const dispatch = useDispatch()
	const { t } = useTranslation('pages')

	const { organizations } = useSuperOrganizationMode()
	const currentOrganizationID = useSelector((state: RootState) => state.profile.profile.data?.currentOrganization?.id)

	const [headerOptions, setHeaderOptions] = useState<IFirmHeader[]>([])
	const [isPrintModalVisible, setIsPrintModalVisible] = useState(false)

	const [query, setQuery] = useQueryParams({
		search: StringParam,
		organizationIDs: withDefault(NumericArrayParam, []),
		users: withDefault(NumericArrayParam, []),
		inflationPartAccepted: withDefault(BooleanParam, null),
		priceOfferFirmHeaderIDs: withDefault(NumericArrayParam, []),
		withoutFirmHeader: withDefault(BooleanParam, null),
		page: withDefault(NumberParam, 1),
		orderBy: withDefault(StringParam, 'address'),
		orderDirection: withDefault(StringParam, 'asc')
	})

	useEffect(() => {
		// TODO: hlavicky s.r.o. su viazane na aktualnu organizaciu - v cross-org pohlade
		// tento zoznam nemusi pokryvat vsetky s.r.o. ostatnych organizacii
		dispatch(getPriceOfferHeaderOptions((data) => {
			setHeaderOptions(data.headers)
		}))
	}, [dispatch])

	const { data, isFetching } = useSuperOrganizationJobs({
		page: query.page,
		limit: PAGINATION.defaultPageSize,
		search: query.search,
		organizationIDs: (query.organizationIDs || []) as number[],
		users: (query.users || []) as number[],
		priceOfferFirmHeaderIDs: query.withoutFirmHeader ? [] : ((query.priceOfferFirmHeaderIDs || []) as number[]),
		withoutFirmHeader: query.withoutFirmHeader || false,
		inflationPartAccepted: query.inflationPartAccepted,
		orderBy: query.orderBy,
		orderDirection: query.orderDirection
	})

	const jobs = data?.jobs || []
	const monthlyTurnoverTotal = data?.pagination?.monthlyTurnoverTotal || 0
	const totalCount = data?.pagination?.totalCount || 0
	const avgTurnoverPerJob = totalCount ? monthlyTurnoverTotal / totalCount : 0
	const activeOrganizationsCount = (query.organizationIDs || []).length || organizations.length

	const getSortOrder = (orderByValue: string) => {
		if (query.orderBy !== orderByValue) {
			return undefined
		}
		return query.orderDirection === 'desc' ? 'descend' : 'ascend'
	}

	const openJobDetail = (record: SuperOrganizationJobTableItem) => {
		const detailPath = `${i18next.t('paths:jobDetail|path')}/${get(record, 'id')}`

		if (!record.organization || record.organization.id === currentOrganizationID) {
			history.push(detailPath)
			return
		}

		// Detail zakazky je na backende scope-nuty na jednu organizaciu, takze bez prepnutia
		// kontextu by skoncil chybou. Preto ponukneme prepnutie.
		Modal.confirm({
			title: t('pages:Zákazka patrí do inej organizácie'),
			content: `${t('pages:Táto zákazka patrí do organizácie')} ${record.organization.name}. ${t('pages:Pre otvorenie detailu je potrebné prepnúť sa do nej. Prepnúť?')}`,
			okText: t('pages:Prepnúť'),
			cancelText: t('pages:Zrušiť'),
			onOk: () => {
				dispatch(switchProfile(record.organization!.id, () => {
					history.push(detailPath)
					window.location.reload()
				}))
			}
		})
	}

	const columns: Columns = [
		{
			title: i18next.t('pages:Adresa'),
			dataIndex: 'address',
			key: 'address',
			ellipsis: true,
			width: 110,
			sorter: true,
			sortOrder: getSortOrder('address'),
			render: (value) => value || '-'
		},
		{
			title: i18next.t('pages:Organizácia'),
			dataIndex: 'organization',
			key: 'organization',
			ellipsis: true,
			width: 130,
			sorter: true,
			sortOrder: getSortOrder('organizationName'),
			render: renderOrganizationTag
		},
		{
			title: i18next.t('pages:Zodpovedné osoby'),
			dataIndex: 'trustees',
			key: 'trustees',
			ellipsis: true,
			width: 250,
			render: renderTrustees
		},
		{
			title: i18next.t('pages:s.r.o.'),
			dataIndex: 'firmHeader',
			key: 'firmHeader',
			ellipsis: true,
			width: 170,
			sorter: true,
			sortOrder: getSortOrder('firmName'),
			render(value: IJobFirmHeader | null) {
				return value?.firmName || '-'
			}
		},
		{
			title: i18next.t('pages:Trvanie'),
			dataIndex: 'qrScan',
			// v povodnej tabulke maju oba qrScan stlpce rovnaky key - tu musia byt unikatne
			key: 'qrScanDuration',
			ellipsis: true,
			align: 'center',
			width: 30,
			render: renderQrScanDuration
		},
		{
			title: i18next.t('pages:Posledná služba'),
			dataIndex: 'qrScan',
			key: 'qrScanLastService',
			ellipsis: true,
			align: 'right',
			width: 30,
			render: renderQrScanLastService
		}
	]

	const handleTableChange = (
		pagination: TablePaginationConfig,
		filters: Record<string, FilterValue | null>,
		sorter: SorterResult<SuperOrganizationJobTableItem> | SorterResult<SuperOrganizationJobTableItem>[]
	) => {
		const sorterLocal = sorter as SorterResult<any>
		const mappedOrderBy = SORTER_FIELD_TO_ORDER_BY[String(sorterLocal?.field)]

		const order = (sorterLocal?.order && mappedOrderBy)
			? { orderBy: mappedOrderBy, orderDirection: sorterLocal.order === 'ascend' ? 'asc' : 'desc' }
			: { orderBy: 'address', orderDirection: 'asc' }

		setQuery({
			...query,
			page: pagination.current || 1,
			...order
		})
	}

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debounced = useCallback(debounce((searchTerm: string) => setQuery({ ...query, search: searchTerm, page: 1 }), 300), [query])

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		debounced(e.target.value)
	}

	return (
		<div className={'page-wrapper'}>
			<div className={'flex'} style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'flex-start', gap: 16, minHeight: '80px' }}>
				<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, flex: '1 1 auto', minWidth: 0 }}>
					<Select
						mode={'multiple'}
						allowClear
						showSearch
						style={{ minWidth: 260 }}
						placeholder={t('pages:Filter podľa organizácie')}
						value={(query.organizationIDs || []) as number[]}
						optionFilterProp={'children'}
						filterOption={(input, option) => String(option?.children ?? '').toLowerCase().indexOf(input.toLowerCase()) >= 0}
						onChange={(values: number[]) => setQuery({ ...query, organizationIDs: values, page: 1 })}
					>
						{organizations.map((organization) => (
							<Select.Option key={organization.id} value={organization.id}>
								{organization.name}
							</Select.Option>
						))}
					</Select>
					<Input.Search onChange={handleSearchChange} style={{ width: 260 }} allowClear />
					<SelectUsersField
						onValueChange={(userData) => {
							setQuery({
								...query,
								users: userData.value.map((item) => (item.value)),
								page: 1
							})
						}}
						userValues={query.users}
						multiple={false}
						placeholder={t('pages:Vyber zamestnanca')}
						queryParams={{ role: [USER_ROLE.USER, USER_ROLE.ADMIN, USER_ROLE.SUPERADMIN] }}
					/>
					<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						<span className={'font-semibold'}>{t('pages:Len s aktívnou inflačnou doložkou')}</span>
						<Switch
							onChange={(value: boolean) => setQuery({ ...query, inflationPartAccepted: value, page: 1 })}
							checked={query?.inflationPartAccepted || false}
						/>
					</div>
					<Select
						mode={'multiple'}
						allowClear
						showSearch
						style={{ minWidth: 240 }}
						placeholder={t('pages:Filter podľa s.r.o.')}
						value={query.withoutFirmHeader ? [WITHOUT_FIRM_HEADER] : ((query.priceOfferFirmHeaderIDs || []) as number[])}
						optionFilterProp={'children'}
						filterOption={(input, option) => String(option?.children ?? '').toLowerCase().indexOf(input.toLowerCase()) >= 0}
						onChange={(values: number[]) => {
							const hasWithout = values.includes(WITHOUT_FIRM_HEADER)
							const specifics = values.filter((value) => value !== WITHOUT_FIRM_HEADER)
							if (hasWithout && specifics.length > 0) {
								if (query.withoutFirmHeader) {
									setQuery({ ...query, withoutFirmHeader: null, priceOfferFirmHeaderIDs: specifics, page: 1 })
								} else {
									setQuery({ ...query, withoutFirmHeader: true, priceOfferFirmHeaderIDs: [], page: 1 })
								}
							} else if (hasWithout) {
								setQuery({ ...query, withoutFirmHeader: true, priceOfferFirmHeaderIDs: [], page: 1 })
							} else {
								setQuery({ ...query, withoutFirmHeader: null, priceOfferFirmHeaderIDs: specifics, page: 1 })
							}
						}}
					>
						<Select.Option key={'without'} value={WITHOUT_FIRM_HEADER}>
							{t('pages:Bez s.r.o.')}
						</Select.Option>
						{headerOptions.map((header) => (
							<Select.Option key={header.id} value={header.id}>
								{header.firmName}
							</Select.Option>
						))}
					</Select>
				</div>
				<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'flex-end', gap: 8, flex: '0 1 auto' }}>
					{/* akcne tlacidla viazane na jednu organizaciu (preberaci protokol, QR kody,
					    obraty po s.r.o.) sa v super rezime zamerne nezobrazuju */}
					<Button
						icon={<PrinterOutlined />}
						type={'primary'}
						onClick={() => setIsPrintModalVisible(true)}
					>
						{t('pages:Tlačiť zoznam')}
					</Button>
					<Button
						icon={<PlusCircleOutlined />}
						href={t('paths:jobCreate|path')}
						type={'primary'}
					>
						{t('pages:Pridať Zákazku')}
					</Button>
				</div>
			</div>
			<div className={'flex'} style={{ gap: 12, marginTop: 8, marginBottom: 16 }}>
				<Card size={'small'} style={{ flex: 1 }}>
					<div style={{ fontSize: 13, color: 'rgba(0, 0, 0, 0.45)' }}>
						{t('pages:Mesačný obrat')} · {activeOrganizationsCount} {t('pages:organizácie')}
					</div>
					<div style={{ fontSize: 24, fontWeight: 500, marginTop: 4 }}>{formatEur(monthlyTurnoverTotal)}</div>
				</Card>
				<Card size={'small'} style={{ flex: 1 }}>
					<div style={{ fontSize: 13, color: 'rgba(0, 0, 0, 0.45)' }}>{t('pages:Počet zákaziek')}</div>
					<div style={{ fontSize: 24, fontWeight: 500, marginTop: 4 }}>{totalCount}</div>
				</Card>
				<Card size={'small'} style={{ flex: 1 }}>
					<div style={{ fontSize: 13, color: 'rgba(0, 0, 0, 0.45)' }}>{t('pages:Priemer / zákazka')}</div>
					<div style={{ fontSize: 24, fontWeight: 500, marginTop: 4 }}>{formatEur(avgTurnoverPerJob)}</div>
				</Card>
			</div>
			<Table
				className={'general-table'}
				columns={columns}
				dataSource={jobs}
				onChange={handleTableChange}
				style={{ marginTop: 0 }}
				showSorterTooltip={false}
				pagination={{
					pageSize: PAGINATION.defaultPageSize,
					total: totalCount,
					current: data?.pagination?.page || query.page,
					showSizeChanger: false
				}}
				loading={isFetching}
				onRow={(record) => ({
					onClick: () => openJobDetail(record)
				})}
				locale={{
					emptyText: (
						<Empty description={t('pages:Žiadne dáta')} />
					)
				}}
				size={'small'}
			/>
			<PrintColumnsModal
				visible={isPrintModalVisible}
				onClose={() => setIsPrintModalVisible(false)}
				queryParams={{
					search: query.search,
					organizationIDs: (query.organizationIDs || []) as number[],
					users: (query.users || []) as number[],
					priceOfferFirmHeaderIDs: query.withoutFirmHeader ? [] : ((query.priceOfferFirmHeaderIDs || []) as number[]),
					withoutFirmHeader: query.withoutFirmHeader || false,
					inflationPartAccepted: query.inflationPartAccepted,
					orderBy: query.orderBy,
					orderDirection: query.orderDirection
				}}
			/>
		</div>
	)
}

export default SuperOrganizationJobs
